import { useRef, type Dispatch, type SetStateAction } from "react";
import { recordAnswer } from "./audit";
import type { AuditRecord } from "../types/audit.types";

type Capture = { type?: string; data?: Record<string, any>; timestamp?: unknown };

/**
 * For the engine's inline vitals steps (BMI, BP): remembers what each step last saved so it refills when the step is
 * revisited, tells the host to update that reading rather than add another, and audits old -> new on every save.
 */
export function useInlineVitalCapture(
  onSaveVitals: ((vital: any) => void) | undefined,
  setAudit: Dispatch<SetStateAction<AuditRecord>>,
) {
  const saved = useRef<Record<string, Capture>>({});

  const save = (nodeId: string, nodeType: any, payload: Capture) => {
    const previous = saved.current[nodeId];
    saved.current[nodeId] = payload;
    setAudit((prev) =>
      recordAnswer(prev, `${nodeId}:vital-saved`, nodeType, {
        vitalType: payload?.type,
        previous: previous?.data ?? null,
        updated: payload?.data ?? null,
      }),
    );
    onSaveVitals?.({ ...payload, updateInPlace: true });
  };

  /** Props for an inline capture node: its save handler, the refill, and any opt-in flag set on the node. */
  const props = (node: { id: string; type: string; plausibilityChecks?: boolean }) => ({
    onSave: (payload: any) => save(node.id, node.type, payload),
    initialCapture: saved.current[node.id],
    ...(node.plausibilityChecks === true ? { plausibilityChecks: true } : {}),
  });

  return { props, save };
}
